import { useRef, useEffect, useMemo, useState } from 'react';
import { HOUR_HEIGHT_PX } from '../types';
import type { EventInstance } from '../types';
import type { TimeFormat } from '../types';
import {
  positionTimedEvents,
  isToday,
  formatDateKey,
  formatTime,
  findOverlappingEventIds,
  findInsufficientBreaks,
  startOfDay,
  endOfDay,
} from '../utils';
import { TimeGridEvent } from './TimeGridEvent';

interface TimeGridProps {
  days: Date[];
  events: EventInstance[];
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onClickSlot: (date: Date, hour: number) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  hourHeightPx?: number;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// iOS-style slot interaction: hold to create, drag to scroll.
const LONG_PRESS_MS = 500;
const MOVE_CANCEL_THRESHOLD_PX = 5;
// Window of recent pointer samples used to compute release velocity.
const VELOCITY_SAMPLE_WINDOW_MS = 100;
// Friction normalized to one 60fps frame. Lower = stops faster.
const MOMENTUM_FRICTION_PER_16MS = 0.95;
// Below this absolute velocity (px/ms) the momentum loop exits.
const MOMENTUM_MIN_VELOCITY = 0.05;

export function TimeGrid({
  days,
  events,
  workingHoursStart,
  timeFormat = '12h',
  onClickSlot,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  hourHeightPx: hourHeight = HOUR_HEIGHT_PX,
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
}: TimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const momentumRafRef = useRef<number | null>(null);

  // Cancel any in-flight momentum on unmount.
  useEffect(
    () => () => {
      if (momentumRafRef.current !== null) cancelAnimationFrame(momentumRafRef.current);
    },
    [],
  );

  // Compute which events have conflicts across all visible days
  const conflictingIds = useMemo(() => {
    if (!showConflictWarnings) return new Set<string>();
    const allIds = new Set<string>();
    for (const day of days) {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayEvents = events.filter((e) => {
        if (e.isAllDay) return false;
        const s = new Date(e.startAt);
        const end = new Date(e.endAt);
        return s < dayEnd && end > dayStart;
      });
      const ids = findOverlappingEventIds(dayEvents);
      ids.forEach((id) => allIds.add(id));
    }
    return allIds;
  }, [showConflictWarnings, days, events]);

  // Compute insufficient break gaps per day
  const breakWarningsByDay = useMemo(() => {
    if (minBreakBetweenEvents <= 0)
      return new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    const map = new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    for (const day of days) {
      const warnings = findInsufficientBreaks(events, day, minBreakBetweenEvents);
      if (warnings.length > 0) {
        map.set(formatDateKey(day), warnings);
      }
    }
    return map;
  }, [minBreakBetweenEvents, days, events]);

  // Now-line: re-render every minute so the indicator slides down through
  // the day. Only the today column renders it (see `isToday(day)` below).
  const [now, setNow] = useState(() => new Date());
  const showsToday = useMemo(() => days.some((d) => isToday(d)), [days]);
  useEffect(() => {
    if (!showsToday) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [showsToday]);
  const nowTopPx = (now.getHours() + now.getMinutes() / 60) * hourHeight;

  function startMomentum(el: HTMLElement, initialVelocity: number) {
    if (Math.abs(initialVelocity) < MOMENTUM_MIN_VELOCITY) return;
    let v = initialVelocity;
    let lastT = performance.now();

    const tick = (t: number) => {
      const dt = t - lastT;
      lastT = t;
      const before = el.scrollTop;
      el.scrollTop = before - v * dt;
      // Boundary hit — content didn't move, kill the loop.
      if (el.scrollTop === before) {
        momentumRafRef.current = null;
        return;
      }
      // Friction is defined per 16ms (one 60fps frame); normalize for
      // actual dt so a stalled tick doesn't change the deceleration curve.
      v *= Math.pow(MOMENTUM_FRICTION_PER_16MS, dt / 16);
      if (Math.abs(v) < MOMENTUM_MIN_VELOCITY) {
        momentumRafRef.current = null;
        return;
      }
      momentumRafRef.current = requestAnimationFrame(tick);
    };

    momentumRafRef.current = requestAnimationFrame(tick);
  }

  // Slot pointer handler: a quick press does nothing (matches iOS); a
  // 500ms hold without movement fires onClickSlot; mouse drag scrolls
  // the grid manually (touch scroll is browser-native, so we just
  // cancel the long-press timer and let pointercancel finish us off).
  // Mouse release with velocity → momentum scroll until friction stops it.
  const handleSlotPointerDown =
    (day: Date, hour: number) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Tapping mid-momentum should freeze the calendar, just like grabbing
      // a flicked iOS scroll view.
      if (momentumRafRef.current !== null) {
        cancelAnimationFrame(momentumRafRef.current);
        momentumRafRef.current = null;
      }
      const scrollEl = scrollRef.current;
      const startY = e.clientY;
      const startX = e.clientX;
      const initialScrollTop = scrollEl?.scrollTop ?? 0;
      const isMouse = e.pointerType === 'mouse';

      let phase: 'pending' | 'scrolling' | 'done' = 'pending';
      // Rolling buffer of recent pointer samples (only kept while scrolling
      // a mouse drag) — used to derive release velocity for the flick.
      const samples: Array<{ y: number; t: number }> = [];

      const timer = window.setTimeout(() => {
        if (phase !== 'pending') return;
        phase = 'done';
        cleanup();
        onClickSlot(day, hour);
      }, LONG_PRESS_MS);

      const onMove = (mv: PointerEvent) => {
        if (phase === 'done') return;
        const dy = mv.clientY - startY;
        const dx = mv.clientX - startX;
        if (
          phase === 'pending' &&
          Math.abs(dx) < MOVE_CANCEL_THRESHOLD_PX &&
          Math.abs(dy) < MOVE_CANCEL_THRESHOLD_PX
        ) {
          return;
        }
        if (phase === 'pending') {
          window.clearTimeout(timer);
          phase = isMouse && scrollEl ? 'scrolling' : 'done';
        }
        if (phase === 'scrolling' && scrollEl) {
          scrollEl.scrollTop = initialScrollTop - dy;
          const t = performance.now();
          samples.push({ y: mv.clientY, t });
          const cutoff = t - VELOCITY_SAMPLE_WINDOW_MS;
          while (samples.length > 1 && samples[0].t < cutoff) samples.shift();
        }
      };

      const onUp = () => {
        if (phase === 'scrolling' && scrollEl && samples.length >= 2) {
          const first = samples[0];
          const last = samples[samples.length - 1];
          const dt = last.t - first.t;
          if (dt > 0) {
            // px/ms in pointer-Y space. Pointer down-motion = positive,
            // which decreased scrollTop, so the momentum loop subtracts.
            const velocity = (last.y - first.y) / dt;
            startMomentum(scrollEl, velocity);
          }
        }
        cleanup();
      };

      const cleanup = () => {
        if (phase === 'pending') window.clearTimeout(timer);
        phase = 'done';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

  // Auto-scroll on mount and when the visible range changes (switching
  // Day↔Week, navigating dates). If today is in view, anchor the current
  // time about a third of the way down the viewport so the upcoming
  // hours are visible below the now-line. Otherwise fall back to
  // working hours. `daysKey` is a stable signal — `days` is a new array
  // reference on every render.
  const daysKey = useMemo(() => days.map((d) => formatDateKey(d)).join('|'), [days]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (showsToday) {
      const n = new Date();
      const nowHour = n.getHours() + n.getMinutes() / 60;
      const targetTop = nowHour * hourHeight - el.clientHeight / 3;
      el.scrollTop = Math.max(0, targetTop);
    } else {
      el.scrollTop = Math.max(0, workingHoursStart * hourHeight - 20);
    }
  }, [workingHoursStart, hourHeight, showsToday, daysKey]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      <div className="flex" style={{ minHeight: 24 * hourHeight }}>
        {/* Hour labels */}
        <div className="w-14 shrink-0">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="text-[10px] text-muted-foreground text-right pr-2 relative"
              style={{ height: hourHeight }}
            >
              <span className="absolute -top-[7px] right-2">
                {hour === 0 ? '' : formatTime(new Date(2000, 0, 1, hour), timeFormat)}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div
          className="flex-1 grid relative"
          style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
        >
          {days.map((day) => {
            const dayKey = formatDateKey(day);
            const positioned = positionTimedEvents(events, day, hourHeight);
            const todayClass = isToday(day) ? 'bg-primary/5' : '';

            return (
              <div key={dayKey} className={`border-r last:border-r-0 relative ${todayClass}`}>
                {/* Hour grid lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    role="button"
                    tabIndex={0}
                    aria-label={formatTime(new Date(2000, 0, 1, hour), timeFormat)}
                    className="border-b border-dashed border-muted hover:bg-muted/30 select-none touch-pan-y"
                    style={{ height: hourHeight }}
                    onPointerDown={handleSlotPointerDown(day, hour)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClickSlot(day, hour);
                      }
                    }}
                  />
                ))}

                {/* Positioned events */}
                {positioned.map((pos) => (
                  <TimeGridEvent
                    key={pos.event.id}
                    positioned={pos}
                    onClick={() => onClickEvent(pos.event)}
                    timeFormat={timeFormat}
                    resizingHeight={resizingEventId === pos.event.id ? resizeHeight : undefined}
                    onResizeStart={onResizeStart}
                    dimPastEvents={dimPastEvents}
                    eventColorPriority={eventColorPriority}
                    hasConflict={conflictingIds.has(pos.event.eventId)}
                  />
                ))}

                {/* Now-line — only on today's column */}
                {isToday(day) && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: nowTopPx - 1, height: 2 }}
                    aria-hidden="true"
                  >
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
                    <div className="h-0.5 w-full bg-red-500" />
                  </div>
                )}

                {/* Insufficient break indicators */}
                {breakWarningsByDay.get(dayKey)?.map((w, i) => {
                  const endBefore = new Date(w.eventBefore.endAt);
                  const startAfter = new Date(w.eventAfter.startAt);
                  const midMinutes =
                    (endBefore.getHours() * 60 +
                      endBefore.getMinutes() +
                      startAfter.getHours() * 60 +
                      startAfter.getMinutes()) /
                    2;
                  const topPx = (midMinutes / 60) * hourHeight;

                  return (
                    <div
                      key={`break-${i}`}
                      className="absolute left-1 right-1 flex items-center justify-center pointer-events-none z-10"
                      style={{ top: topPx - 6, height: 12 }}
                      title={`${w.gapMinutes}min break`}
                    >
                      <div className="border-t border-dashed border-amber-400 dark:border-amber-500 w-full" />
                      <span className="absolute bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-[9px] px-1 rounded">
                        {w.gapMinutes}m
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
